import React, { useState, useEffect } from 'react';
import { XMarkIcon, PlusIcon, TrashIcon, PhotoIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import toast from 'react-hot-toast';
import { cn } from '../utils';

const TaskAssignment = ({ isOpen, onClose, onTaskCreated }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium',
    assignedTo: '',
    assignedToGroup: '',
    assignmentType: 'individual',
    dueDate: '',
    estimatedDuration: '',
    isCarRelated: false,
    car: '',
    tags: [],
    templateId: '',
    useTemplate: false
  });

  const [mechanics, setMechanics] = useState([]);
  const [groups, setGroups] = useState([]);
  const [cars, setCars] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateData, setTemplateData] = useState({});
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [selectedImages, setSelectedImages] = useState([]);
  const [imagePreviewUrls, setImagePreviewUrls] = useState([]);

  // Fetch mechanics, groups, cars and templates on component mount
  useEffect(() => {
    if (isOpen) {
      fetchMechanics();
      fetchGroups();
      fetchCars();
      fetchTemplates();
    }
  }, [isOpen]);

  const fetchMechanics = async () => {
    try {
      const response = await apiService.getMechanics();
      if (response.success) {
        setMechanics(response.mechanics || []);
      }
    } catch (error) {
      console.error('Error fetching mechanics:', error);
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await apiService.getGroups({ limit: 50 });
      if (response.success) {
        setGroups(response.groups || []);
      }
    } catch (error) {
      console.error('Error fetching groups:', error);
    }
  };

  const fetchCars = async () => {
    try {
      const response = await apiService.getCars({ limit: 100 });
      if (response.success) {
        setCars(response.cars || []);
      }
    } catch (error) {
      console.error('Error fetching cars:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await apiService.getTemplates({ isActive: true, limit: 50 });
      if (response.success) {
        setTemplates(response.templates || []);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Handle assignment type changes
    if (name === 'assignmentType') {
      if (value === 'individual') {
        setFormData(prev => ({ ...prev, assignedToGroup: '' }));
      } else if (value === 'group') {
        setFormData(prev => ({ ...prev, assignedTo: '' }));
      }
    }

    // Clear car if not car-related
    if (name === 'isCarRelated' && !checked) {
      setFormData(prev => ({ ...prev, car: '' }));
    }

    // Handle template selection
    if (name === 'templateId') {
      const template = templates.find(t => t._id === value);
      setSelectedTemplate(template);
      if (template) {
        setFormData(prev => ({
          ...prev,
          title: `${template.name} - Fahrzeuginspektion`,
          description: `Aufgabe basiert auf Vorlage: ${template.name}. Template-Daten werden vom Mechaniker ausgefüllt.`,
          tags: [...prev.tags.filter(tag => !['template', 'inspection', 'service'].includes(tag)), 'template', template.type],
          useTemplate: true
        }));
        // Template data will be filled by mechanic, not admin
        setTemplateData({});
      } else {
        setSelectedTemplate(null);
        setTemplateData({});
        setFormData(prev => ({
          ...prev,
          useTemplate: false
        }));
      }
    }

    // Clear errors for the field being edited
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const getDefaultFieldValue = (field) => {
    switch (field.fieldType) {
      case 'text':
      case 'textarea':
      case 'email':
      case 'phone':
      case 'url':
        return '';
      case 'number':
      case 'currency':
        return 0;
      case 'boolean':
      case 'checkbox':
        return false;
      case 'radio':
      case 'select':
        return '';
      case 'multiselect':
        return [];
      case 'date':
      case 'datetime':
      case 'time':
        return '';
      case 'image':
      case 'file':
        return null;
      case 'rating':
        return 0;
      default:
        return '';
    }
  };

  const handleTemplateDataChange = (fieldKey, value) => {
    setTemplateData(prev => ({
      ...prev,
      [fieldKey]: value
    }));
  };

  const renderTemplateField = (field) => {
    const value = templateData[field.key];
    
    switch (field.fieldType) {
      case 'text':
      case 'email':
      case 'phone':
      case 'url':
        return (
          <input
            type={field.fieldType === 'email' ? 'email' : field.fieldType === 'phone' ? 'tel' : field.fieldType === 'url' ? 'url' : 'text'}
            value={value || ''}
            onChange={(e) => handleTemplateDataChange(field.key, e.target.value)}
            placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}...`}
            className="w-full px-3 py-2 bg-dark-600 border border-dark-500 rounded-md text-white placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        );
      
      case 'textarea':
        return (
          <textarea
            value={value || ''}
            onChange={(e) => handleTemplateDataChange(field.key, e.target.value)}
            placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}...`}
            rows={3}
            className="w-full px-3 py-2 bg-dark-600 border border-dark-500 rounded-md text-white placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        );
      
      case 'number':
      case 'currency':
        return (
          <input
            type="number"
            value={value || ''}
            onChange={(e) => handleTemplateDataChange(field.key, parseFloat(e.target.value) || 0)}
            placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}...`}
            min={field.min}
            max={field.max}
            step={field.step || (field.fieldType === 'currency' ? '0.01' : '1')}
            className="w-full px-3 py-2 bg-dark-600 border border-dark-500 rounded-md text-white placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        );
      
      case 'boolean':
      case 'checkbox':
        return (
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={!!value}
              onChange={(e) => handleTemplateDataChange(field.key, e.target.checked)}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-dark-500 rounded bg-dark-600"
            />
            <span className="ml-2 text-sm text-dark-300">
              {field.description || 'Check if applicable'}
            </span>
          </div>
        );
      
      case 'radio':
        return (
          <div className="space-y-2">
            {field.options?.map((option) => (
              <div key={option.value} className="flex items-center">
                <input
                  type="radio"
                  name={field.key}
                  value={option.value}
                  checked={value === option.value}
                  onChange={(e) => handleTemplateDataChange(field.key, e.target.value)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-dark-500 bg-dark-600"
                />
                <span className="ml-2 text-sm text-white">{option.label}</span>
              </div>
            ))}
          </div>
        );
      
      case 'select':
        return (
          <select
            value={value || ''}
            onChange={(e) => handleTemplateDataChange(field.key, e.target.value)}
            className="w-full px-3 py-2 bg-dark-600 border border-dark-500 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">Select an option...</option>
            {field.options?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );
      
      case 'date':
        return (
          <input
            type="date"
            value={value || ''}
            onChange={(e) => handleTemplateDataChange(field.key, e.target.value)}
            className="w-full px-3 py-2 bg-dark-600 border border-dark-500 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        );
      
      case 'datetime':
        return (
          <input
            type="datetime-local"
            value={value || ''}
            onChange={(e) => handleTemplateDataChange(field.key, e.target.value)}
            className="w-full px-3 py-2 bg-dark-600 border border-dark-500 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        );
      
      case 'time':
        return (
          <input
            type="time"
            value={value || ''}
            onChange={(e) => handleTemplateDataChange(field.key, e.target.value)}
            className="w-full px-3 py-2 bg-dark-600 border border-dark-500 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        );
      
      case 'rating':
        return (
          <div className="flex items-center space-x-2">
            {[1, 2, 3, 4, 5].map((rating) => (
              <button
                key={rating}
                type="button"
                onClick={() => handleTemplateDataChange(field.key, rating)}
                className={`text-2xl ${
                  value >= rating ? 'text-yellow-400' : 'text-dark-500'
                } hover:text-yellow-300 transition-colors`}
              >
                ★
              </button>
            ))}
            <span className="text-sm text-dark-300 ml-2">
              {value || 0}/5
            </span>
          </div>
        );
      
      default:
        return (
          <input
            type="text"
            value={value || ''}
            onChange={(e) => handleTemplateDataChange(field.key, e.target.value)}
            placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}...`}
            className="w-full px-3 py-2 bg-dark-600 border border-dark-500 rounded-md text-white placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        );
    }
  };

  const handleTagAdd = (e) => {
    if (e.key === 'Enter' && e.target.value.trim()) {
      e.preventDefault();
      const tag = e.target.value.trim();
      if (!formData.tags.includes(tag)) {
        setFormData(prev => ({
          ...prev,
          tags: [...prev.tags, tag]
        }));
        e.target.value = '';
      }
    }
  };

  const handleTagRemove = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    const maxFiles = 10;
    const maxSize = 10 * 1024 * 1024; // 10MB per file

    if (files.length > maxFiles) {
      toast.error(`Maximum ${maxFiles} images allowed`);
      return;
    }

    const validFiles = files.filter(file => {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image file`);
        return false;
      }
      if (file.size > maxSize) {
        toast.error(`${file.name} is too large. Maximum size is 10MB`);
        return false;
      }
      return true;
    });

    setSelectedImages(validFiles);

    // Create preview URLs
    const previewUrls = validFiles.map(file => URL.createObjectURL(file));
    setImagePreviewUrls(previewUrls);
  };

  const removeImage = (index) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviewUrls(prev => {
      URL.revokeObjectURL(prev[index]); // Clean up memory
      return prev.filter((_, i) => i !== index);
    });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) newErrors.title = 'Title is required';
    
    // Validate assignment
    if (formData.assignmentType === 'individual') {
      if (!formData.assignedTo) newErrors.assignedTo = 'Please assign to a mechanic';
    } else if (formData.assignmentType === 'group') {
      if (!formData.assignedToGroup) newErrors.assignedToGroup = 'Please assign to a group';
    }
    
    if (formData.isCarRelated && !formData.car) newErrors.car = 'Please select a car';
    if (formData.dueDate && new Date(formData.dueDate) < new Date()) {
      newErrors.dueDate = 'Due date cannot be in the past';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix the errors below');
      return;
    }

    setLoading(true);
    try {
      let response;

      if (formData.useTemplate && formData.templateId) {
        // Create task from template (template data will be filled by mechanic)
        const templateTaskPayload = {
          templateId: formData.templateId,
          taskData: {
            title: formData.title,
            description: formData.description,
            priority: formData.priority,
            assignedTo: formData.assignmentType === 'individual' ? formData.assignedTo : undefined,
            assignedToGroup: formData.assignmentType === 'group' ? formData.assignedToGroup : undefined,
            car: formData.isCarRelated ? formData.car : undefined,
            dueDate: formData.dueDate || undefined,
            estimatedDuration: formData.estimatedDuration ? parseInt(formData.estimatedDuration) : undefined,
            tags: formData.tags
          },
          templateData: {} // Empty - to be filled by mechanic
        };

        response = await apiService.createTaskFromTemplate(
          templateTaskPayload.templateId,
          templateTaskPayload.taskData,
          templateTaskPayload.templateData
        );
      } else {
        // Create regular task
        const taskPayload = {
          ...formData,
          assignedTo: formData.assignmentType === 'individual' ? formData.assignedTo : undefined,
          assignedToGroup: formData.assignmentType === 'group' ? formData.assignedToGroup : undefined,
          tags: formData.tags,
          car: formData.isCarRelated ? formData.car : undefined,
          estimatedDuration: formData.estimatedDuration ? parseInt(formData.estimatedDuration) : undefined
        };

        response = await apiService.createTask(taskPayload);
      }
      
      if (response.success) {
        const createdTask = response.task;
        
        // Upload images if any selected
        if (selectedImages.length > 0) {
          try {
            const formDataImages = new FormData();
            selectedImages.forEach(file => {
              formDataImages.append('files', file);
            });

            const uploadResponse = await apiService.uploadTaskImages(createdTask.id, formDataImages);
            if (uploadResponse.success) {
              toast.success(`Task created with ${selectedImages.length} image(s)`);
            } else {
              toast.success('Task created but failed to upload some images');
            }
          } catch (uploadError) {
            console.error('Image upload error:', uploadError);
            toast.success('Task created but failed to upload images');
          }
        } else {
          toast.success('Task created successfully');
        }

        resetForm();
        onTaskCreated?.(createdTask);
        onClose();
      } else {
        throw new Error(response.message || 'Failed to create task');
      }
    } catch (error) {
      console.error('Task creation error:', error);
      toast.error(error.message || 'Failed to create task');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      priority: 'medium',
      assignedTo: '',
      assignedToGroup: '',
      assignmentType: 'individual',
      dueDate: '',
      estimatedDuration: '',
      isCarRelated: false,
      car: '',
      tags: [],
      templateId: '',
      useTemplate: false
    });
    setErrors({});
    setSelectedImages([]);
    setImagePreviewUrls([]);
    setSelectedTemplate(null);
    setTemplateData({});
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-dark-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-dark-600">
          <h2 className="text-xl font-semibold text-white">Create New Task</h2>
          <button
            onClick={onClose}
            className="text-dark-400 hover:text-white transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Title */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-white mb-2">
                Task Title *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                className={cn(
                  "w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-md text-white placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent",
                  errors.title && "border-red-500"
                )}
                placeholder="Enter task title..."
              />
              {errors.title && <p className="text-red-500 text-sm mt-1">{errors.title}</p>}
            </div>

            {/* Priority */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Priority
              </label>
              <select
                name="priority"
                value={formData.priority}
                onChange={handleInputChange}
                className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>

            {/* Template Selection */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-white mb-2">
                Use Template (Optional)
              </label>
              <select
                name="templateId"
                value={formData.templateId}
                onChange={handleInputChange}
                className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Select a template (optional)</option>
                {templates.map(template => (
                  <option key={template._id} value={template._id}>
                    {template.name} - {template.type} ({template.language?.toUpperCase()})
                  </option>
                ))}
              </select>
              {selectedTemplate && (
                <div className="mt-2 p-3 bg-dark-700 rounded-md">
                  <p className="text-sm text-dark-300 mb-2">{selectedTemplate.description}</p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-primary-600 text-white text-xs rounded">
                      {selectedTemplate.type}
                    </span>
                    <span className="px-2 py-1 bg-dark-600 text-white text-xs rounded">
                      {selectedTemplate.sections?.length || 0} sections
                    </span>
                    <span className="px-2 py-1 bg-dark-600 text-white text-xs rounded">
                      {selectedTemplate.sections?.reduce((total, section) => 
                        total + (section.fields?.length || 0), 0
                      ) || 0} fields
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Assignment Type */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-white mb-2">
                Assignment Type *
              </label>
              <div className="flex space-x-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="assignmentType"
                    value="individual"
                    checked={formData.assignmentType === 'individual'}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-dark-600"
                  />
                  <span className="ml-2 text-sm text-white">Individual Mechanic</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="assignmentType"
                    value="group"
                    checked={formData.assignmentType === 'group'}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-dark-600"
                  />
                  <span className="ml-2 text-sm text-white">Group of Mechanics</span>
                </label>
              </div>
            </div>

            {/* Assigned To - Individual */}
            {formData.assignmentType === 'individual' && (
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Assign To *
                </label>
                <select
                  name="assignedTo"
                  value={formData.assignedTo}
                  onChange={handleInputChange}
                  className={cn(
                    "w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent",
                    errors.assignedTo && "border-red-500"
                  )}
                >
                  <option value="">Select mechanic...</option>
                  <option value={user?.id}>{user?.name} (Myself)</option>
                  {mechanics.map(mechanic => (
                    <option key={mechanic._id} value={mechanic._id}>
                      {mechanic.name} ({mechanic.email})
                    </option>
                  ))}
                </select>
                {errors.assignedTo && <p className="text-red-500 text-sm mt-1">{errors.assignedTo}</p>}
              </div>
            )}

            {/* Assigned To - Group */}
            {formData.assignmentType === 'group' && (
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Assign To Group *
                </label>
                <select
                  name="assignedToGroup"
                  value={formData.assignedToGroup}
                  onChange={handleInputChange}
                  className={cn(
                    "w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent",
                    errors.assignedToGroup && "border-red-500"
                  )}
                >
                  <option value="">Select group...</option>
                  {groups.map(group => (
                    <option key={group._id} value={group._id}>
                      {group.name} ({group.memberCount || group.members?.length || 0} members)
                    </option>
                  ))}
                </select>
                {errors.assignedToGroup && <p className="text-red-500 text-sm mt-1">{errors.assignedToGroup}</p>}
                {formData.assignedToGroup && (
                  <p className="text-xs text-dark-400 mt-1">
                    All group members will be able to work on this task
                  </p>
                )}
              </div>
            )}

            {/* Due Date */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Due Date
              </label>
              <input
                type="datetime-local"
                name="dueDate"
                value={formData.dueDate}
                onChange={handleInputChange}
                min={new Date().toISOString().slice(0, 16)}
                className={cn(
                  "w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent",
                  errors.dueDate && "border-red-500"
                )}
              />
              {errors.dueDate && <p className="text-red-500 text-sm mt-1">{errors.dueDate}</p>}
            </div>

            {/* Estimated Duration */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Estimated Duration (minutes)
              </label>
              <input
                type="number"
                name="estimatedDuration"
                value={formData.estimatedDuration}
                onChange={handleInputChange}
                min="0"
                className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="e.g., 60"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={4}
              className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-md text-white placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Describe the task in detail..."
            />
          </div>

          {/* Car-related section */}
          <div className="border border-dark-600 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-4">
              <input
                type="checkbox"
                id="isCarRelated"
                name="isCarRelated"
                checked={formData.isCarRelated}
                onChange={handleInputChange}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-dark-600 rounded"
              />
              <label htmlFor="isCarRelated" className="text-sm font-medium text-white">
                This task is related to a specific car
              </label>
            </div>

            {formData.isCarRelated && (
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Select Car *
                </label>
                <select
                  name="car"
                  value={formData.car}
                  onChange={handleInputChange}
                  className={cn(
                    "w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent",
                    errors.car && "border-red-500"
                  )}
                >
                  <option value="">Select a car...</option>
                  {cars.map(car => (
                    <option key={car._id} value={car._id}>
                      {car.numberPlate} - {car.make} {car.model} ({car.year})
                    </option>
                  ))}
                </select>
                {errors.car && <p className="text-red-500 text-sm mt-1">{errors.car}</p>}
              </div>
            )}
          </div>

          {/* Template Preview (Admin can see what mechanic will fill) */}
          {selectedTemplate && selectedTemplate.sections && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-white">Template-Felder (werden vom Mechaniker ausgefüllt)</h3>
              <div className="bg-dark-700 rounded-lg p-4">
                <p className="text-sm text-dark-300 mb-4">
                  Diese Felder werden vom zugewiesenen Mechaniker bei der Bearbeitung der Aufgabe ausgefüllt:
                </p>
                {selectedTemplate.sections.map((section, sectionIndex) => (
                  <div key={section.key} className="mb-4">
                    <h4 className="text-md font-medium text-primary-400 mb-2">{section.name}</h4>
                    {section.description && (
                      <p className="text-xs text-dark-300 mb-2">{section.description}</p>
                    )}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {section.fields?.map((field, fieldIndex) => (
                        <div key={field.key} className="text-xs bg-dark-600 rounded px-2 py-1">
                          <span className="text-white">{field.label}</span>
                          <span className="text-dark-400 ml-1">({field.fieldType})</span>
                          {field.required && <span className="text-red-400 ml-1">*</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Image Upload */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Task Images (Optional)
            </label>
            <div className="border-2 border-dashed border-dark-600 rounded-lg p-6 text-center">
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handleImageSelect}
                className="hidden"
                id="image-upload"
                disabled={loading}
              />
              <label
                htmlFor="image-upload"
                className="cursor-pointer flex flex-col items-center space-y-2"
              >
                <PhotoIcon className="h-8 w-8 text-dark-400" />
                <span className="text-sm text-dark-300">
                  Click to select images (up to 10 files, 10MB each)
                </span>
              </label>
            </div>

            {/* Image Previews */}
            {imagePreviewUrls.length > 0 && (
              <div className="mt-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {imagePreviewUrls.map((url, index) => (
                    <div key={index} className="relative">
                      <img
                        src={url}
                        alt={`Preview ${index + 1}`}
                        className="w-full h-24 object-cover rounded-md"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                      >
                        <TrashIcon className="h-3 w-3" />
                      </button>
                      <span className="text-xs text-dark-300 mt-1 block truncate">
                        {selectedImages[index]?.name}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Tags
            </label>
            <input
              type="text"
              placeholder="Type a tag and press Enter"
              onKeyDown={handleTagAdd}
              className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-md text-white placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            {formData.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleTagRemove(tag)}
                      className="ml-1 text-primary-600 hover:text-primary-800"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end space-x-4 pt-6 border-t border-dark-600">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-dark-300 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Creating...</span>
                </>
              ) : (
                <>
                  <PlusIcon className="h-4 w-4" />
                  <span>Create Task</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TaskAssignment;